import React from "react";
import { motion } from "framer-motion";

/**
 * HeroSplit — full-bleed rural photo with soft navy vignette on the left
 * (for text legibility) blending seamlessly into the sharp right side.
 * The address search CARD sits centered inside a glass-morphism container.
 */
export default function HeroSplit({ children }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#0a1229", minHeight: "640px" }}
      data-testid="hero-split"
    >
      {/* ---- SHARP PHOTO LAYER (full background) ---- */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 hero-kenburns"
        style={{
          backgroundImage: "url(/hero-homes.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      />

      {/* ---- Left gradient vignette for copy legibility ---- */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(100deg, rgba(10,18,41,0.88) 0%, rgba(10,18,41,0.80) 32%, rgba(10,18,41,0.55) 55%, rgba(10,18,41,0.18) 78%, rgba(10,18,41,0.05) 100%)",
        }}
      />
      {/* Bottom fade to improve readability on mobile */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-1/3 z-[1] pointer-events-none"
        style={{
          background: "linear-gradient(to top, rgba(10,18,41,0.55), rgba(10,18,41,0))",
        }}
      />

      {/* ---- Subtle wavy accent line running down the divide ---- */}
      <svg
        aria-hidden
        className="absolute inset-0 w-full h-full z-[2] pointer-events-none hidden md:block"
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7ed957" stopOpacity="0" />
            <stop offset="30%" stopColor="#7ed957" stopOpacity="0.25" />
            <stop offset="70%" stopColor="#7ed957" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#7ed957" stopOpacity="0" />
          </linearGradient>
          <filter id="wave-blur">
            <feGaussianBlur stdDeviation="0.8" />
          </filter>
        </defs>
        <path
          d="M720,0 C690,140 760,260 700,380 C650,490 760,590 700,720 C680,760 720,800 720,800"
          stroke="url(#wave-gradient)"
          strokeWidth="2"
          fill="none"
          filter="url(#wave-blur)"
        />
      </svg>

      {/* ---- Soft green radial glow (brand accent) ---- */}
      <div
        aria-hidden
        className="absolute z-[2] pointer-events-none"
        style={{
          top: "-10%",
          left: "-8%",
          width: 520,
          height: 520,
          background:
            "radial-gradient(closest-side, rgba(126,217,87,0.14), rgba(126,217,87,0) 70%)",
          filter: "blur(6px)",
        }}
      />

      {/* ---- Grain ---- */}
      <div
        aria-hidden
        className="absolute inset-0 z-[3] pointer-events-none mix-blend-overlay"
        style={{
          opacity: 0.05,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* ---- Animated energy line accent (bottom-right) ---- */}
      <svg
        className="absolute -right-10 bottom-8 z-[3] hidden md:block pointer-events-none"
        width="380"
        height="200"
        viewBox="0 0 380 200"
        fill="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="velcor-flow-split" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7ed957" stopOpacity="0" />
            <stop offset="50%" stopColor="#7ed957" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7ed957" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d="M -40 180 C 80 130, 160 70, 260 50 C 320 30, 360 10, 400 -20"
          stroke="url(#velcor-flow-split)"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
        />
      </svg>

      {/* ---- Content slot ---- */}
      <div className="relative z-10 text-white">{children}</div>
    </section>
  );
}
